<template>
    <a :href="postUrl" class="flex flex-col h-full mosaic-item group">
        <div class="relative overflow-hidden rounded-sm" :style="{ aspectRatio: featured ? '16 / 10' : '16 / 9' }">
            <img v-if="image" :src="image" :alt="post.title" class="w-full h-full !object-cover mosaic-image" />
            <span v-if="post.primary_category" class="mosaic-badge">
                <span class="pi pi-tag" style="font-size: 10px" />
                {{ post.primary_category.name }}
            </span>
        </div>

        <h3 v-if="featured"
            class="mt-4 mb-0 text-2xl font-bold leading-tight underline text-gray-900 decoration-2 underline-offset-4 group-hover:decoration-primary"
            style="font-family: Georgia, 'Times New Roman', serif;">
            {{ post.title }}
        </h3>
        <h4 v-else class="mt-3 mb-0 text-lg font-bold leading-snug text-gray-900 line-clamp-2 group-hover:underline"
            style="font-family: Georgia, 'Times New Roman', serif;">
            {{ post.title }}
        </h4>

        <div v-if="post.created_at" class="mt-1 text-xs tracking-wide text-gray-500 uppercase"
            :class="{ 'mt-2': featured }">
            {{ formatDate(post.created_at) }}
        </div>

        <p v-if="featured && post.excerpt" class="mt-3 mb-0 text-gray-600 line-clamp-3">
            {{ post.excerpt }}
        </p>

        <span v-if="featured" class="inline-block mt-4 text-sm font-medium underline text-primary underline-offset-2">
            Read more &rarr;
        </span>
    </a>
</template>

<script lang="ts" setup>
import { Post } from '@/types';
import { computed } from 'vue';
import { usePage } from '@inertiajs/vue3';
import { replaceS3WithCloudfront } from '@/utils/helpers';

const props = defineProps<{
    post: Post;
    // Featured cards show a larger image, bigger title, excerpt and read-more.
    featured?: boolean;
}>();

const page = usePage();

const image = computed<string | undefined>(() => {
    if (!props.post?.featured_image) return undefined;
    return replaceS3WithCloudfront(props.post.featured_image, page.props.__awsBucket as string, page.props.__awsUrl as string);
});

const postUrl = computed<string>(() => {
    if (!props.post) return '#';

    if (props.post.primary_category) {
        return route('show.post.with.category', {
            post_category: props.post.primary_category.slug,
            post: props.post.slug,
        });
    }
    // Category-less posts live at the bare /inspiration/<slug>.
    return route('show.category', { post_category: props.post.slug });
});

const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
};
</script>

<style lang="scss" scoped>
.mosaic-item {
    .mosaic-image {
        transform: scale(1);
        transition: 0.25s;
    }

    &:hover .mosaic-image {
        transform: scale(1.05);
        transition: 0.5s;
    }
}

.mosaic-badge {
    position: absolute;
    bottom: 0.5rem;
    left: 0.5rem;
    display: inline-flex;
    align-items: center;
    gap: 0.25rem;
    padding: 0.25rem 0.5rem;
    border-radius: 2px;
    background: rgba(0, 0, 0, 0.7);
    color: #fff;
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.04em;
}

.line-clamp-2 {
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
}

.line-clamp-3 {
    display: -webkit-box;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
    overflow: hidden;
}
</style>
